import { TextField, InputAdornment, IconButton } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { WidgetProps } from "@rjsf/utils";
import { useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { inputStyles, renderLabel, renderDescriptionTooltip } from "./common";

dayjs.extend(utc);

// Date Widget for date-only input
export const DateWidget = (props: WidgetProps) => {
  const { id, value, label, onChange, required, disabled, readonly, schema } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    if (val === "") {
      onChange(undefined);
      return;
    }
    
    let finalValue = val;
    // Parse as local date (date input provides local date)
    const selectedDate = dayjs(val);
    
    // Format date according to schema pattern if available
    // Note: For date-only fields, we keep the date in local timezone
    // since there's no time component to convert to UTC
    if (schema.pattern === "^\\d{2}[-/]\\d{2}[-/]\\d{4}$") {
      finalValue = selectedDate.format("MM-DD-YYYY");
    } else if (schema.pattern === "^\\d{4}-\\d{2}-\\d{2}$") {
      finalValue = selectedDate.format("YYYY-MM-DD");
    }
    
    onChange(finalValue);
  };

  const getDisplayValue = (val: string | undefined) => {
    if (!val) return "";
    // Parse the date and display in local time
    // For date-only values, dayjs will use the local date
    const date = dayjs(val);
    return date.isValid() ? date.format("YYYY-MM-DD") : "";
  };

  return (
    <TextField
      id={id}
      fullWidth
      type="date"
      value={getDisplayValue(value)}
      onChange={handleChange}
      disabled={disabled}
      inputRef={inputRef}
      slotProps={{
        input: {
          readOnly: readonly,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  if (inputRef.current && "showPicker" in inputRef.current) {
                    (inputRef.current as any).showPicker();
                  }
                }}
                edge="end"
                size="small"
                sx={{ mr: schema.description ? 1 : 0 }}
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
              {schema.description && renderDescriptionTooltip(schema.description)}
            </InputAdornment>
          ),
        },
        inputLabel: {
          shrink: true,
        },
      }}
      label={renderLabel(label, !!required)}
      required={required}
      size="small"
      variant="outlined"
      sx={inputStyles}
    />
  );
};
